/**
 * Utility functions for image processing and upload
 */

/**
 * Converts and compresses an image file to PNG format with transparency support
 * @param file Original image file
 * @returns Promise with the compressed PNG blob
 */
export const convertToWebP = async (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let width = img.width;
      let height = img.height;
      const maxDimension = 1000; // Limit maximum dimension

      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
      }

      // Set canvas size to the new dimensions
      canvas.width = width;
      canvas.height = height;

      // Enable image smoothing for better quality
      if (ctx) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
      }

      // Clear canvas and draw image
      ctx?.clearRect(0, 0, width, height);
      ctx?.drawImage(img, 0, 0, width, height);

      // Convert to PNG with compression
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to convert image to PNG'));
          }
        },
        'image/png',
        0.7 // Compression level
      );
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Generates a clean filename for storage
 * @param originalName Original filename
 * @param orgId Organization ID
 * @returns Clean filename with org prefix
 */
export const generateCleanFilename = (originalName: string, orgId: string): string => {
  // Remove special characters and spaces, convert to lowercase
  const cleanName = originalName
    .toLowerCase()
    .replace(/[^a-z0-9.]/g, '-')
    .replace(/-+/g, '-');

  // Get timestamp for uniqueness
  const timestamp = Date.now();

  // Return format: org_id/logo_timestamp_cleanname.png
  return `${orgId}/logo_${timestamp}_${cleanName}.png`;
};
